import React from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { NavigateNext as NavigateNextIcon } from "@mui/icons-material";

/**
 * A container component for pages with consistent padding, title, and optional breadcrumbs
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Page title
 * @param {Array} props.breadcrumbs - Array of breadcrumb objects with text and path properties
 * @param {React.ReactNode} props.actions - Optional actions to display next to the title
 * @param {Object} props.sx - Additional styles to apply to the container
 */
const PageContainer = ({
  children,
  title,
  breadcrumbs = [],
  actions,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        width: "100%",
        mb: 4,
        ...sx,
      }}
    >
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;

            return isLast ? (
              <Typography key={crumb.text} color="text.primary">
                {crumb.text}
              </Typography>
            ) : (
              <Link
                key={crumb.text}
                component={RouterLink}
                to={crumb.path}
                underline="hover"
                color="inherit"
              >
                {crumb.text}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: isMobile ? "column" : "row",
          mb: 3,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          sx={{
            fontWeight: 500,
            mb: isMobile && actions ? 2 : 0,
          }}
        >
          {title}
        </Typography>

        {actions && <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box>}
      </Box>

      {children}
    </Box>
  );
};

export default PageContainer;
